import { useCallback, useMemo, useRef, useState } from 'react';

function deepEqual(a: unknown, b: unknown): boolean {
  // JSON-serialisable compare is good enough for the form payloads we care
  // about (strings, numbers, booleans, arrays, plain objects). NaN / Infinity
  // / undefined keys / Date instances would round-trip oddly; we don't use
  // any of those in `APReprocessEdits`.
  return JSON.stringify(a) === JSON.stringify(b);
}

export interface UseDirtyForm<T> {
  value: T;
  setValue: (next: T) => void;
  patch: (partial: Partial<T>) => void;
  isDirty: boolean;
  reset: (newInitial?: T) => void;
}

export function useDirtyForm<T>(initial: T): UseDirtyForm<T> {
  const initialRef = useRef<T>(initial);
  const [value, setValueState] = useState<T>(initial);

  const setValue = useCallback((next: T) => {
    setValueState(next);
  }, []);

  const patch = useCallback((partial: Partial<T>) => {
    setValueState((prev) => ({ ...(prev as object), ...(partial as object) } as T));
  }, []);

  const reset = useCallback((newInitial?: T) => {
    const target = newInitial ?? initialRef.current;
    initialRef.current = target;
    setValueState(target);
  }, []);

  const isDirty = useMemo(() => !deepEqual(value, initialRef.current), [value]);

  return { value, setValue, patch, isDirty, reset };
}
